import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResizableTableHead, useResizableColumns } from "@/components/ui/resizable-table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip as STooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { LogOut, Users, Bell, BarChart3, Plus, Trash2, Edit2, Loader2, Check, DollarSign, FileText, Eye, RefreshCw, Send, UserPlus, ChevronLeft, ChevronRight, ArrowUpDown, Filter, Mail, Clock, Megaphone, TrendingUp, Activity, Moon, Sun, Zap, BookOpen, MapPin, ArrowRightLeft, X, Sparkles, ShieldCheck, Palette, Download } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import LeadsPanel from "../components/LeadsPanel";
import BlogPanel from "../components/BlogPanel";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import { SECTORS, getSectorLabel, getGroupedSectors } from "@/data/sectors";

const PLANS = { la_vitrine: "La Vitrine Active", la_vitrine_dynamique: "La Formule Croissance" };
const OPTIONS_MAP = { fidelite_marketing_plus: "Fidélité & Marketing +", visibilite_seo: "Visibilité SEO" };
const STATUT_LABELS = { envoye: "Envoyé", relance: "Relancé", converti: "Converti", sans_reponse: "Sans réponse" };

function Tip({ label, children }) {
  return (
    <STooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="bg-[#2F4F4F] text-white text-[11px] px-2.5 py-1 rounded-md">{label}</TooltipContent>
    </STooltip>
  );
}

function DaysRemainingBadge({ dateEnvoi }) {
  if (!dateEnvoi) return null;
  const elapsed = Math.floor((Date.now() - new Date(dateEnvoi).getTime()) / 86400000);
  const remaining = Math.max(15 - elapsed, 0);
  let color, bg, border;
  if (remaining > 10) { color = "text-[#2F4F4F]"; bg = "bg-[#2F4F4F]/10"; border = "border-[#2F4F4F]/20"; }
  else if (remaining >= 5) { color = "text-[#3F6B6B]"; bg = "bg-[#3F6B6B]/10"; border = "border-[#3F6B6B]/20"; }
  else { color = "text-red-600"; bg = "bg-red-50"; border = "border-red-200"; }
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${color} ${bg} ${border}`}>
      <Clock className="w-3 h-3" />{remaining === 0 ? "Expire" : `${remaining}j`}
    </span>
  );
}
const STATUT_COLORS = { envoye: "bg-blue-50 text-blue-700 border-blue-200", relance: "bg-amber-50 text-amber-700 border-amber-200", converti: "bg-[#2F4F4F]/10 text-[#2F4F4F] border-[#2F4F4F]/20", sans_reponse: "bg-gray-100 text-gray-500 border-gray-200" };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const { dark, toggle: toggleTheme } = useTheme();
  const [tab, setTab] = useState("overview");
  const prospectsCols = useResizableColumns("admin-prospects", { entreprise: 220, email: 220, type: 110, date: 110, demo: 90, statut: 150, actions: 100 });
  const clientsCols = useResizableColumns("admin-clients", { nom: 160, email: 220, societe: 180, sector: 150, forfait: 140, options: 160, contrat: 100, date: 100, fin: 120, actions: 100 });
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsPages, setClientsPages] = useState(1);
  const [clientsSort, setClientsSort] = useState("created_at");
  const [clientsSortOrder, setClientsSortOrder] = useState("desc");
  const [clientsPlanFilter, setClientsPlanFilter] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [prospectsTotal, setProspectsTotal] = useState(0);
  const [prospectsPage, setProspectsPage] = useState(1);
  const [prospectsPages, setProspectsPages] = useState(1);
  const [prospectsDemoFilter, setProspectsDemoFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [viewDialog, setViewDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [prospectDialog, setProspectDialog] = useState(false);
  const [prospectEditDialog, setProspectEditDialog] = useState(null);
  const [prospectDeleteDialog, setProspectDeleteDialog] = useState(null);
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailProspectDialog, setEmailProspectDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [planChangeRequests, setPlanChangeRequests] = useState([]);
  const [pendingPlanCount, setPendingPlanCount] = useState(0);
  const [planChangeStatusFilter, setPlanChangeStatusFilter] = useState("pending");
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [planDirectDialog, setPlanDirectDialog] = useState(null); // client à modifier (admin direct)
  const [planDirectPlan, setPlanDirectPlan] = useState("");
  const [planDirectOptions, setPlanDirectOptions] = useState([]);
  const [plansCatalog, setPlansCatalog] = useState(null);
  const [brandGuideOpen, setBrandGuideOpen] = useState(false);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); }
    catch (err) { console.warn("loadStats failed:", err?.message); }
  }, []);

  const loadPlansCatalog = useCallback(async () => {
    try { const { data } = await api.get("/plans"); setPlansCatalog(data); }
    catch (err) { console.warn("loadPlansCatalog failed:", err?.message); }
  }, []);

  const loadPlanChangeRequests = useCallback(async () => {
    try {
      const url = planChangeStatusFilter ? `/admin/plan-change-requests?status_filter=${planChangeStatusFilter}` : "/admin/plan-change-requests";
      const { data } = await api.get(url);
      setPlanChangeRequests(data.requests || []);
    } catch (err) { console.warn("loadPlanChangeRequests failed:", err?.message); }
  }, [planChangeStatusFilter]);

  const loadPendingPlanCount = useCallback(async () => {
    try { const { data } = await api.get("/admin/plan-change-requests/pending-count"); setPendingPlanCount(data.pending || 0); }
    catch (err) { console.warn("loadPendingPlanCount failed:", err?.message); }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: clientsPage, limit: 25, sort_by: clientsSort, sort_order: clientsSortOrder });
      if (clientsPlanFilter) params.append("plan_filter", clientsPlanFilter);
      const { data } = await api.get(`/admin/clients?${params}`);
      setClients(data.clients); setClientsTotal(data.total); setClientsPages(data.pages);
    } catch (err) { console.warn("loadClients failed:", err?.message); toast.error("Erreur de chargement des clients"); }
  }, [clientsPage, clientsSort, clientsSortOrder, clientsPlanFilter]);

  const loadNotifications = useCallback(async () => {
    try { const { data } = await api.get("/admin/notifications"); setNotifications(data); }
    catch (err) { console.warn("loadNotifications failed:", err?.message); }
  }, []);

  const loadProspects = useCallback(async () => {
    try {
      let url = `/admin/prospects?page=${prospectsPage}&limit=25`;
      if (prospectsDemoFilter) url += `&demo_type=${prospectsDemoFilter}`;
      const { data } = await api.get(url);
      setProspects(data.prospects); setProspectsTotal(data.total); setProspectsPages(data.pages);
    } catch (err) { console.warn("loadProspects failed:", err?.message); }
  }, [prospectsPage, prospectsDemoFilter]);

  useEffect(() => {
    Promise.all([loadStats(), loadClients(), loadNotifications(), loadProspects(), loadPlansCatalog(), loadPendingPlanCount()]).finally(() => setLoading(false));
  }, [loadStats, loadClients, loadNotifications, loadProspects, loadPlansCatalog, loadPendingPlanCount]);

  useEffect(() => { loadPlanChangeRequests(); }, [loadPlanChangeRequests]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const toggleSort = (field) => {
    if (clientsSort === field) setClientsSortOrder(o => o === "asc" ? "desc" : "asc");
    else { setClientsSort(field); setClientsSortOrder("asc"); }
    setClientsPage(1);
  };

  const handleMarkRead = async (id) => { await api.put(`/admin/notifications/${id}/read`); loadNotifications(); };
  const handleMarkAllRead = async () => { await api.put("/admin/notifications/read-all"); loadNotifications(); };
  const handleDeleteClient = async () => {
    if (!deleteDialog) return;
    try { await api.delete(`/admin/clients/${deleteDialog.id}`); toast.success("Client supprimé"); setDeleteDialog(null); loadClients(); loadStats(); }
    catch { toast.error("Erreur"); }
  };

  // Prospect actions
  const handleRelance = async (id) => {
    setActionLoading(true);
    try { const { data } = await api.post(`/admin/prospects/${id}/relance`); toast.success(data.message); loadProspects(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActionLoading(false); }
  };
  const handleConvert = async (id) => {
    try {
      const { data } = await api.post(`/admin/prospects/${id}/convert`);
      toast.success("Prospect converti ! Créez maintenant le compte client.");
      // Set prefill data BEFORE opening dialog
      setTempPassword(null);
      setConvertPrefill(data.prefill);
      setCreateDialog(true);
      loadProspects(); loadStats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [convertPrefill, setConvertPrefill] = useState(null);
  const handleDeleteProspect = async () => {
    if (!prospectDeleteDialog) return;
    try { await api.delete(`/admin/prospects/${prospectDeleteDialog.id}`); toast.success("Prospect supprimé"); setProspectDeleteDialog(null); loadProspects(); loadStats(); }
    catch { toast.error("Erreur"); }
  };
  const handleUpdateProspectStatus = async (id, statut) => {
    try { await api.put(`/admin/prospects/${id}`, { statut }); loadProspects(); loadStats(); }
    catch (err) { console.warn("updateProspectStatus failed:", err?.message); toast.error("Mise à jour impossible"); }
  };

  // Plan change requests handlers
  const handleApprovePlanChange = async (req) => {
    if (!window.confirm(`Approuver la demande de ${req.user_name} ?\n\nForfait : ${PLANS[req.current_plan] || req.current_plan} → ${PLANS[req.requested_plan] || req.requested_plan}\n\nCela mettra à jour Stripe (proration) et générera un nouveau contrat à signer par le client (engagement initial de 6 mois).`)) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/plan-change-requests/${req.id}/approve`);
      toast.success("Demande approuvée — Stripe + DB + email mis à jour.");
      await Promise.all([loadPlanChangeRequests(), loadPendingPlanCount(), loadClients(), loadNotifications()]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'approbation.");
    } finally { setActionLoading(false); }
  };

  const handleRejectPlanChange = async () => {
    if (!rejectDialog) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/plan-change-requests/${rejectDialog.id}/reject`, { reason: rejectReason });
      toast.success("Demande refusée et client notifié.");
      setRejectDialog(null);
      setRejectReason("");
      await Promise.all([loadPlanChangeRequests(), loadPendingPlanCount(), loadNotifications()]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally { setActionLoading(false); }
  };

  const openDirectPlanChange = (client) => {
    setPlanDirectDialog(client);
    setPlanDirectPlan(client.plan || "");
    setPlanDirectOptions(client.options || []);
  };

  const handleSubmitDirectPlanChange = async () => {
    if (!planDirectDialog || !planDirectPlan) return;
    if (!window.confirm(`Confirmer le changement de forfait pour ${planDirectDialog.prenom} ${planDirectDialog.nom} ?\n\nNouveau forfait : ${PLANS[planDirectPlan] || planDirectPlan}\nOptions : ${planDirectOptions.length ? planDirectOptions.map(o => OPTIONS_MAP[o] || o).join(", ") : "aucune"}\n\nCela met à jour Stripe, invalide l'ancien contrat et envoie un email au client (engagement initial de 6 mois).`)) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/clients/${planDirectDialog.id}/change-plan`, {
        new_plan: planDirectPlan,
        new_options: planDirectOptions,
        notify_client: true,
      });
      toast.success("Changement de plan exécuté.");
      setPlanDirectDialog(null);
      await Promise.all([loadClients(), loadPlanChangeRequests(), loadPendingPlanCount(), loadNotifications()]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors du changement.");
    } finally { setActionLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2F4F4F]" /></div>;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="mbs-dash min-h-screen bg-[#FBF9F5]">
      <header className="border-b border-[#BFBFBF] bg-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/api/static/images/web/logo_mbs.png" alt="" className="h-8 w-8 object-contain rounded-lg ring-1 ring-[#D4AF37]/25 shadow-sm bg-white flex-shrink-0" />
            <span className="text-lg font-semibold text-[#2F4F4F] tracking-tight truncate" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>MonBeauSite</span>
            <Badge className="bg-[#2F4F4F]/10 text-[#2F4F4F] border-[#2F4F4F]/20 text-xs flex-shrink-0">Admin</Badge>
          </div>
          <div className="flex items-center gap-4">
            <Tip label={dark ? "Mode clair" : "Mode sombre"}>
              <button data-testid="theme-toggle" onClick={toggleTheme} className="text-[#6B6B6B] hover:text-[#2F4F4F] transition-colors p-1.5 rounded-full hover:bg-[#2F4F4F]/5">
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </Tip>
            <button data-testid="admin-notifications-btn" onClick={() => setTab("notifications")} className="relative text-[#6B6B6B] hover:text-[#2F4F4F]">
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-[#2F4F4F] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{unreadCount}</span>}
            </button>
            <button data-testid="admin-brand-guide-btn" onClick={() => setBrandGuideOpen(true)} title="Générer un guide d'identité visuelle" className="relative text-[#6B6B6B] hover:text-[#C46A4A]">
              <Palette className="w-5 h-5" />
            </button>
            <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={logout} className="text-[#6B6B6B] hover:text-[#2F4F4F] hover:bg-[#2F4F4F]/5"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <p className="section-num font-mono-tech text-xs tracking-widest text-tech mb-2 inline-flex">ADMIN // CONSOLE v2.0</p>
        <h1 className="text-3xl font-semibold mb-2 text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Tableau de bord</h1>
        <p className="text-[#6B6B6B] text-sm mb-10">Gestion de vos clients, prospects et abonnements.</p>

        <Tabs value={tab} onValueChange={setTab} className="space-y-8">
          <TabsList className="bg-white border border-[#BFBFBF] p-1 rounded-full flex justify-start overflow-x-auto no-scrollbar gap-0.5">
            <TabsTrigger value="overview" data-testid="admin-tab-overview" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
              <BarChart3 className="w-4 h-4 mr-2" /> Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="prospects" data-testid="admin-tab-prospects" className="data-[state=active]:bg-[#3F6B6B] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
              <Send className="w-4 h-4 mr-2" /> Prospection
              {stats?.total_prospects > 0 && <span className="ml-1 bg-[#3F6B6B]/20 text-[#3F6B6B] data-[state=active]:bg-white/20 data-[state=active]:text-white text-[10px] px-1.5 rounded-full">{stats.total_prospects}</span>}
            </TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
              <Zap className="w-4 h-4 mr-2" /> Leads Auto
            </TabsTrigger>
            <TabsTrigger value="clients" data-testid="admin-tab-clients" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
              <Users className="w-4 h-4 mr-2" /> Clients actifs
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="admin-tab-notifications" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0 relative">
              <Bell className="w-4 h-4 mr-2" /> Notifications
              {unreadCount > 0 && <span className="ml-1 bg-[#3F6B6B] text-white text-[10px] px-1.5 rounded-full">{unreadCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="plan-changes" data-testid="admin-tab-plan-changes" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0 relative">
              <ArrowRightLeft className="w-4 h-4 mr-2" /> Changements
              {pendingPlanCount > 0 && <span className="ml-1 bg-[#C46A4A] text-white text-[10px] px-1.5 rounded-full">{pendingPlanCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="contrat" data-testid="admin-tab-contrat" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
              <FileText className="w-4 h-4 mr-2" /> Contrat
            </TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="admin-tab-newsletter" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
              <Megaphone className="w-4 h-4 mr-2" /> Newsletter
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
              <BarChart3 className="w-4 h-4 mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog" className="data-[state=active]:bg-[#2F4F4F] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-3 md:px-5 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
              <BookOpen className="w-4 h-4 mr-2" /> Blog
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { label: "Clients actifs", value: stats?.total_clients || 0, icon: Users },
                { label: "Contrats signés", value: stats?.active_clients || 0, icon: FileText },
                { label: "Prospects", value: stats?.total_prospects || 0, icon: Send, color: "text-[#3F6B6B]" },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-[#BFBFBF] rounded-xl p-5">
                  <s.icon className={`w-4 h-4 mb-3 ${s.accent ? "text-[#2F4F4F]" : s.color || "text-[#6B6B6B]/40"}`} />
                  <p className={`text-2xl font-bold ${s.accent ? "text-[#2F4F4F]" : "text-[#2F4F4F]"}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{s.value}</p>
                  <p className="text-xs text-[#6B6B6B] mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="bg-white border border-[#3F6B6B]/20 rounded-xl p-5">
                <Mail className="w-4 h-4 mb-3 text-[#3F6B6B]" />
                <p className="text-2xl font-bold text-[#3F6B6B]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{stats?.prospects_envoyes || 0}</p>
                <p className="text-xs text-[#6B6B6B] mt-1">Prospects en attente</p>
              </div>
              <div className="bg-white border border-[#2F4F4F]/20 rounded-xl p-5">
                <UserPlus className="w-4 h-4 mb-3 text-[#2F4F4F]" />
                <p className="text-2xl font-bold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{stats?.prospects_convertis || 0}</p>
                <p className="text-xs text-[#6B6B6B] mt-1">Convertis</p>
              </div>
            </div>
          </TabsContent>

          {/* PROSPECTS */}
          <TabsContent value="prospects" className="space-y-5">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <h3 className="text-lg font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Prospection</h3>
              <div className="flex items-center gap-3">
                <Select value={prospectsDemoFilter || "all"} onValueChange={(v) => { setProspectsDemoFilter(v === "all" ? "" : v); setProspectsPage(1); }}>
                  <SelectTrigger className="h-8 text-xs border-[#BFBFBF] bg-white rounded-full w-[160px]">
                    <Filter className="w-3 h-3 mr-1 text-[#6B6B6B]" /><SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#BFBFBF]">
                    <SelectItem value="all">Tous les prospects</SelectItem>
                    <SelectItem value="avec_demo">Avec Demo</SelectItem>
                    <SelectItem value="sans_demo">Sans Demo</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="add-prospect-btn" onClick={() => setProspectDialog(true)} className="bg-[#3F6B6B] text-white hover:bg-[#4A6B51] rounded-full" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Nouveau prospect
                </Button>
              </div>
            </div>
            <div className="bg-white border border-[#BFBFBF] rounded-xl overflow-hidden">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="border-[#BFBFBF] hover:bg-transparent">
                    <ResizableTableHead id="entreprise" width={prospectsCols.widths.entreprise} onWidthChange={(w) => prospectsCols.setWidth("entreprise", w)} className="text-[#6B6B6B] text-xs">Entreprise</ResizableTableHead>
                    <ResizableTableHead id="email" width={prospectsCols.widths.email} onWidthChange={(w) => prospectsCols.setWidth("email", w)} className="text-[#6B6B6B] text-xs">Email</ResizableTableHead>
                    <ResizableTableHead id="type" width={prospectsCols.widths.type} onWidthChange={(w) => prospectsCols.setWidth("type", w)} className="text-[#6B6B6B] text-xs">Type</ResizableTableHead>
                    <ResizableTableHead id="date" width={prospectsCols.widths.date} onWidthChange={(w) => prospectsCols.setWidth("date", w)} className="text-[#6B6B6B] text-xs">Date d'envoi</ResizableTableHead>
                    <ResizableTableHead id="demo" width={prospectsCols.widths.demo} onWidthChange={(w) => prospectsCols.setWidth("demo", w)} className="text-[#6B6B6B] text-xs">Demo</ResizableTableHead>
                    <ResizableTableHead id="statut" width={prospectsCols.widths.statut} onWidthChange={(w) => prospectsCols.setWidth("statut", w)} className="text-[#6B6B6B] text-xs">Statut</ResizableTableHead>
                    <ResizableTableHead id="actions" width={prospectsCols.widths.actions} onWidthChange={(w) => prospectsCols.setWidth("actions", w)} className="text-[#6B6B6B] text-xs text-right">Actions</ResizableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prospects.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-[#6B6B6B]/50 py-12 text-sm">Aucun prospect pour le moment</TableCell></TableRow>
                  ) : prospects.map((p) => (
                    <TableRow key={p.id} className="border-[#BFBFBF]">
                      <TableCell className="font-medium text-[#2F4F4F] text-sm">{p.nom_entreprise}</TableCell>
                      <TableCell className="text-[#6B6B6B] text-sm">{p.email}</TableCell>
                      <TableCell>
                        {p.site_demo ? (
                          <Badge data-testid={`badge-demo-${p.id}`} className="bg-[#2F4F4F]/10 text-[#2F4F4F] border-0 text-[10px] font-medium rounded-full px-2 py-0.5">Avec Demo</Badge>
                        ) : (
                          <Badge data-testid={`badge-no-demo-${p.id}`} className="bg-orange-100 text-orange-700 border-0 text-[10px] font-medium rounded-full px-2 py-0.5">Sans Demo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-[#6B6B6B] text-sm">{new Date(p.date_envoi).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell><DaysRemainingBadge dateEnvoi={p.date_envoi} /></TableCell>
                      <TableCell>
                        <Select value={p.statut} onValueChange={(v) => handleUpdateProspectStatus(p.id, v)}>
                          <SelectTrigger className={`h-7 text-xs border rounded-full w-[130px] ${STATUT_COLORS[p.statut] || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-[#BFBFBF]">
                            <SelectItem value="envoye">Envoyé</SelectItem>
                            <SelectItem value="relance">Relancé</SelectItem>
                            <SelectItem value="converti">Converti</SelectItem>
                            <SelectItem value="sans_reponse">Sans réponse</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Tip label="Envoyer un email"><Button data-testid={`email-prospect-${p.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-[#3F6B6B]" onClick={() => setEmailProspectDialog(p)}><Mail className="w-3.5 h-3.5" /></Button></Tip>
                          {p.google_maps_url && <Tip label="Voir la fiche Google Maps"><a href={p.google_maps_url} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon" className="h-8 w-8 text-[#C46A4A] hover:text-[#A6553A] hover:bg-[#C46A4A]/10"><MapPin className="w-3.5 h-3.5" /></Button></a></Tip>}
                          {p.site_demo && <Tip label="Voir le site demo"><a href={p.site_demo} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-[#2F4F4F]"><Eye className="w-3.5 h-3.5" /></Button></a></Tip>}
                          <Tip label="Modifier"><Button data-testid={`edit-prospect-${p.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-[#2F4F4F]" onClick={() => setProspectEditDialog(p)}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button></Tip>
                          {p.statut === "envoye" && (
                            <Tip label="Relancer"><Button data-testid={`relance-prospect-${p.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#3F6B6B] hover:text-[#4A6B51]" onClick={() => handleRelance(p.id)} disabled={actionLoading}>
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button></Tip>
                          )}
                          {p.statut !== "converti" && (
                            <Tip label="Convertir en client"><Button data-testid={`convert-prospect-${p.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#2F4F4F] hover:text-[#1F3535]" onClick={() => handleConvert(p.id)}>
                              <UserPlus className="w-3.5 h-3.5" />
                            </Button></Tip>
                          )}
                          <Tip label="Supprimer"><Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-red-500" onClick={() => setProspectDeleteDialog(p)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button></Tip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {prospectsPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#6B6B6B]">{prospectsTotal} prospect{prospectsTotal > 1 ? "s" : ""} — Page {prospectsPage}/{prospectsPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={prospectsPage <= 1} onClick={() => setProspectsPage(p => p - 1)} className="rounded-full border-[#BFBFBF]"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={prospectsPage >= prospectsPages} onClick={() => setProspectsPage(p => p + 1)} className="rounded-full border-[#BFBFBF]"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </TabsContent>


          {/* LEADS AUTO */}
          <TabsContent value="leads" className="space-y-5">
            <LeadsPanel onConvert={loadProspects} />
          </TabsContent>

          {/* CLIENTS */}
          <TabsContent value="clients" className="space-y-5">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <h3 className="text-lg font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Liste des clients actifs</h3>
              <div className="flex items-center gap-3">
                <Select value={clientsPlanFilter} onValueChange={(v) => { setClientsPlanFilter(v === "all" ? "" : v); setClientsPage(1); }}>
                  <SelectTrigger className="h-8 text-xs border-[#BFBFBF] bg-white rounded-full w-[180px]">
                    <Filter className="w-3 h-3 mr-1 text-[#6B6B6B]" /><SelectValue placeholder="Tous les forfaits" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#BFBFBF]">
                    <SelectItem value="all">Tous les forfaits</SelectItem>
                    <SelectItem value="la_vitrine">La Vitrine Active</SelectItem>
                    <SelectItem value="la_vitrine_dynamique">La Formule Croissance</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="add-client-btn" onClick={() => { setCreateDialog(true); setTempPassword(null); setConvertPrefill(null); }} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Nouveau client
                </Button>
              </div>
            </div>
            <div className="bg-white border border-[#BFBFBF] rounded-xl overflow-hidden">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="border-[#BFBFBF] hover:bg-transparent">
                    <ResizableTableHead id="nom" width={clientsCols.widths.nom} onWidthChange={(w) => clientsCols.setWidth("nom", w)} className="text-[#6B6B6B] text-xs cursor-pointer hover:text-[#2F4F4F]" onClick={() => toggleSort("nom")}>
                      <span className="flex items-center gap-1">Nom <ArrowUpDown className="w-3 h-3" /></span>
                    </ResizableTableHead>
                    <ResizableTableHead id="email" width={clientsCols.widths.email} onWidthChange={(w) => clientsCols.setWidth("email", w)} className="text-[#6B6B6B] text-xs">Email de contact</ResizableTableHead>
                    <ResizableTableHead id="societe" width={clientsCols.widths.societe} onWidthChange={(w) => clientsCols.setWidth("societe", w)} className="text-[#6B6B6B] text-xs cursor-pointer hover:text-[#2F4F4F]" onClick={() => toggleSort("denomination_sociale")}>
                      <span className="flex items-center gap-1">Société <ArrowUpDown className="w-3 h-3" /></span>
                    </ResizableTableHead>
                    <ResizableTableHead id="sector" width={clientsCols.widths.sector} onWidthChange={(w) => clientsCols.setWidth("sector", w)} className="text-[#6B6B6B] text-xs">Secteur</ResizableTableHead>
                    <ResizableTableHead id="forfait" width={clientsCols.widths.forfait} onWidthChange={(w) => clientsCols.setWidth("forfait", w)} className="text-[#6B6B6B] text-xs">Forfait</ResizableTableHead>
                    <ResizableTableHead id="options" width={clientsCols.widths.options} onWidthChange={(w) => clientsCols.setWidth("options", w)} className="text-[#6B6B6B] text-xs">Options</ResizableTableHead>
                    <ResizableTableHead id="contrat" width={clientsCols.widths.contrat} onWidthChange={(w) => clientsCols.setWidth("contrat", w)} className="text-[#6B6B6B] text-xs">Contrat</ResizableTableHead>
                    <ResizableTableHead id="date" width={clientsCols.widths.date} onWidthChange={(w) => clientsCols.setWidth("date", w)} className="text-[#6B6B6B] text-xs cursor-pointer hover:text-[#2F4F4F]" onClick={() => toggleSort("created_at")}>
                      <span className="flex items-center gap-1">Date <ArrowUpDown className="w-3 h-3" /></span>
                    </ResizableTableHead>
                    <ResizableTableHead id="fin" width={clientsCols.widths.fin} onWidthChange={(w) => clientsCols.setWidth("fin", w)} className="text-[#6B6B6B] text-xs">Engagement</ResizableTableHead>
                    <ResizableTableHead id="actions" width={clientsCols.widths.actions} onWidthChange={(w) => clientsCols.setWidth("actions", w)} className="text-[#6B6B6B] text-xs text-right">Actions</ResizableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center text-[#6B6B6B]/50 py-12 text-sm">Aucun client</TableCell></TableRow>
                  ) : clients.map((c) => (
                    <TableRow key={c.id} className="border-[#BFBFBF]">
                      <TableCell className="font-medium text-[#2F4F4F] text-sm">{c.prenom} {c.nom}</TableCell>
                      <TableCell className="text-[#6B6B6B] text-sm">{c.email}</TableCell>
                      <TableCell className="text-[#6B6B6B] text-sm">{c.denomination_sociale}</TableCell>
                      <TableCell data-testid={`client-sector-${c.id}`}>
                        {c.sector ? (
                          <Badge className="text-xs bg-[#C46A4A]/10 text-[#C46A4A] border-[#C46A4A]/20">{getSectorLabel(c.sector)}</Badge>
                        ) : (
                          <span className="text-xs text-[#6B6B6B]/50">—</span>
                        )}
                      </TableCell>
                      <TableCell><Badge className={`text-xs ${c.plan === "la_vitrine_dynamique" ? "bg-[#2F4F4F]/10 text-[#2F4F4F] border-[#2F4F4F]/20" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{PLANS[c.plan] || c.plan}</Badge></TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {c.options && c.options.length > 0 ? c.options.map(o => (
                            <Badge key={o} className="text-xs bg-[#3F6B6B]/10 text-[#3F6B6B] border-[#3F6B6B]/20">{OPTIONS_MAP[o] || o}</Badge>
                          )) : <span className="text-xs text-[#6B6B6B]/50">—</span>}
                        </div>
                      </TableCell>
                      <TableCell>{c.contract_signed ? <Check className="w-4 h-4 text-[#2F4F4F]" /> : <span className="text-xs text-[#3F6B6B]">En attente</span>}</TableCell>
                      <TableCell className="text-[#6B6B6B] text-sm">{new Date(c.created_at).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell className="text-sm">
                        <span className="text-[#6B6B6B] font-mono-tech text-xs">{c.engagement_start ? `6 mois · ${new Date(new Date(c.engagement_start).getTime() + 180 * 86400000).toLocaleDateString("fr-FR")}` : "6 mois"}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {!c.password_changed && (
                            <Tip label="Renvoyer le magic link"><Button data-testid={`resend-magic-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-[#3F6B6B]"
                              onClick={async () => { try { await api.post(`/admin/clients/${c.id}/resend-magic-link`); toast.success(`Magic link renvoyé à ${c.email}`); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } }}>
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button></Tip>
                          )}
                          <Tip label="Envoyer un email"><Button data-testid={`email-client-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-[#3F6B6B]" onClick={() => setEmailDialog(c)}><Mail className="w-3.5 h-3.5" /></Button></Tip>
                          {c.google_maps_url && <Tip label="Voir la fiche Google Maps"><a href={c.google_maps_url} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon" className="h-8 w-8 text-[#C46A4A] hover:text-[#A6553A] hover:bg-[#C46A4A]/10"><MapPin className="w-3.5 h-3.5" /></Button></a></Tip>}
                          <Tip label="Voir la fiche"><Button data-testid={`view-client-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-[#2F4F4F]" onClick={() => setViewDialog(c)}><Eye className="w-3.5 h-3.5" /></Button></Tip>
                          <Tip label="Changer le forfait"><Button data-testid={`change-plan-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-[#C46A4A]" onClick={() => openDirectPlanChange(c)}><ArrowRightLeft className="w-3.5 h-3.5" /></Button></Tip>
                          <Tip label="Modifier"><Button data-testid={`edit-client-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-[#2F4F4F]" onClick={() => setEditDialog(c)}><Edit2 className="w-3.5 h-3.5" /></Button></Tip>
                          <Tip label="Supprimer"><Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B6B6B] hover:text-red-500" onClick={() => setDeleteDialog(c)}><Trash2 className="w-3.5 h-3.5" /></Button></Tip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {clientsPages > 0 && (
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#6B6B6B]">{clientsTotal} client{clientsTotal > 1 ? "s" : ""} — Page {clientsPage}/{clientsPages || 1}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={clientsPage <= 1} onClick={() => setClientsPage(p => p - 1)} className="rounded-full border-[#BFBFBF]"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={clientsPage >= clientsPages} onClick={() => setClientsPage(p => p + 1)} className="rounded-full border-[#BFBFBF]"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* NOTIFICATIONS */}
          <TabsContent value="notifications" className="space-y-5">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Notifications</h3>
              {unreadCount > 0 && <Button data-testid="mark-all-read-btn" variant="outline" size="sm" onClick={handleMarkAllRead} className="border-[#BFBFBF] text-[#6B6B6B] hover:text-[#2F4F4F] rounded-full text-xs"><Check className="w-3 h-3 mr-1" /> Tout marquer lu</Button>}
            </div>
            <ScrollArea className="h-[500px]">
              <div className="space-y-2">
                {notifications.length === 0 ? <p className="text-center text-[#6B6B6B]/50 py-12 text-sm">Aucune notification</p>
                : notifications.map((n) => (
                  <div key={n.id} className={`flex items-start justify-between p-4 rounded-xl border transition-colors ${n.read ? "bg-white border-[#BFBFBF]/50 opacity-60" : "bg-white border-[#2F4F4F]/20"}`}>
                    <div className="flex-1">
                      <p className={`text-sm leading-relaxed ${n.read ? "text-[#6B6B6B]" : "text-[#2F4F4F]"}`}>{n.message}</p>
                      <p className="text-xs text-[#6B6B6B]/50 mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                    </div>
                    {!n.read && <Button variant="ghost" size="sm" onClick={() => handleMarkRead(n.id)} className="text-[#2F4F4F] text-xs hover:bg-[#2F4F4F]/5 ml-2"><Check className="w-3 h-3" /></Button>}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* CONTRACT TEMPLATE */}
          <TabsContent value="contrat" className="space-y-5">
            <ContractEditor />
          </TabsContent>

          {/* PLAN CHANGE REQUESTS */}
          <TabsContent value="plan-changes" className="space-y-5">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Demandes de changement de forfait</h3>
                <p className="text-xs text-[#6B6B6B] mt-1">Validez, refusez ou lancez un changement directement depuis la fiche client.</p>
              </div>
              <Select value={planChangeStatusFilter || "all"} onValueChange={(v) => setPlanChangeStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs border-[#BFBFBF] bg-white rounded-full w-[180px]" data-testid="plan-change-status-filter">
                  <Filter className="w-3 h-3 mr-1 text-[#6B6B6B]" /><SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#BFBFBF]">
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Approuvées</SelectItem>
                  <SelectItem value="rejected">Refusées</SelectItem>
                  <SelectItem value="all">Toutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              {planChangeRequests.length === 0 ? (
                <div className="bg-white border border-[#BFBFBF] rounded-xl p-12 text-center">
                  <ArrowRightLeft className="w-8 h-8 mx-auto mb-3 text-[#6B6B6B]/40" />
                  <p className="text-sm text-[#6B6B6B]">Aucune demande {planChangeStatusFilter === "pending" ? "en attente" : ""}.</p>
                </div>
              ) : planChangeRequests.map((req) => (
                <div key={req.id} data-testid={`plan-change-req-${req.id}`} className="bg-white border border-[#BFBFBF] rounded-xl p-5">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-medium text-[#2F4F4F]">{req.user_name || req.user_email}</p>
                        {req.user_company && <span className="text-xs text-[#6B6B6B]">— {req.user_company}</span>}
                        {req.status === "pending" && <Badge className="bg-[#C46A4A]/10 text-[#C46A4A] border-[#C46A4A]/20 text-[10px]">En attente</Badge>}
                        {req.status === "approved" && <Badge className="bg-[#2F4F4F]/10 text-[#2F4F4F] border-[#2F4F4F]/20 text-[10px]">Approuvée</Badge>}
                        {req.status === "rejected" && <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-[10px]">Refusée</Badge>}
                      </div>
                      <div className="flex items-center gap-3 text-sm flex-wrap">
                        <span className="text-[#6B6B6B]">{PLANS[req.current_plan] || req.current_plan}</span>
                        {req.current_options?.length > 0 && (
                          <span className="text-xs text-[#6B6B6B]/70">+ {req.current_options.map(o => OPTIONS_MAP[o] || o).join(", ")}</span>
                        )}
                        <ArrowRightLeft className="w-3.5 h-3.5 text-[#C46A4A]" />
                        <span className="text-[#2F4F4F] font-medium">{PLANS[req.requested_plan] || req.requested_plan}</span>
                        {req.requested_options?.length > 0 && (
                          <span className="text-xs text-[#2F4F4F]/70">+ {req.requested_options.map(o => OPTIONS_MAP[o] || o).join(", ")}</span>
                        )}
                      </div>
                      {req.message && (
                        <div className="mt-3 bg-[#FBF9F5] border-l-2 border-[#C46A4A] pl-3 py-2 rounded-r-md">
                          <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-0.5">Message du client</p>
                          <p className="text-sm text-[#2F4F4F] italic">{req.message}</p>
                        </div>
                      )}
                      {req.rejection_reason && (
                        <div className="mt-3 bg-red-50 border-l-2 border-red-300 pl-3 py-2 rounded-r-md">
                          <p className="text-xs text-red-700 uppercase tracking-wider mb-0.5">Motif du refus</p>
                          <p className="text-sm text-red-800 italic">{req.rejection_reason}</p>
                        </div>
                      )}
                      <p className="text-xs text-[#6B6B6B]/60 mt-2">Soumise le {new Date(req.created_at).toLocaleString("fr-FR")}{req.processed_at ? ` • Traitée le ${new Date(req.processed_at).toLocaleString("fr-FR")}` : ""}</p>
                    </div>
                    {req.status === "pending" && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button data-testid={`approve-plan-${req.id}`} size="sm" onClick={() => handleApprovePlanChange(req)} disabled={actionLoading} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full text-xs">
                          <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Approuver
                        </Button>
                        <Button data-testid={`reject-plan-${req.id}`} size="sm" variant="outline" onClick={() => { setRejectDialog(req); setRejectReason(""); }} className="border-[#BFBFBF] text-[#6B6B6B] hover:text-red-600 hover:border-red-300 rounded-full text-xs">
                          <X className="w-3.5 h-3.5 mr-1.5" /> Refuser
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="newsletter" className="space-y-5">
            <NewsletterTool />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-5">
            <AnalyticsDashboard />
          </TabsContent>

          <TabsContent value="blog" className="space-y-5">
            <BlogPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* PROSPECT DIALOG */}
      <ProspectCreateDialog open={prospectDialog} onClose={() => setProspectDialog(false)} onSuccess={() => { loadProspects(); loadStats(); }} />
      <BrandGuideDialog open={brandGuideOpen} onClose={() => setBrandGuideOpen(false)} />

      {/* PROSPECT EDIT DIALOG */}
      {prospectEditDialog && <ProspectEditDialog prospect={prospectEditDialog} onClose={() => setProspectEditDialog(null)} onSuccess={() => { setProspectEditDialog(null); loadProspects(); }} />}

      {/* PROSPECT DELETE DIALOG */}
      <Dialog open={!!prospectDeleteDialog} onOpenChange={() => setProspectDeleteDialog(null)}>
        <DialogContent className="bg-white border-[#BFBFBF]">
          <DialogHeader><DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Supprimer le prospect</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B6B6B]">Êtes-vous sûr de vouloir supprimer <strong>{prospectDeleteDialog?.nom_entreprise}</strong> ?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProspectDeleteDialog(null)} className="border-[#BFBFBF] rounded-full">Annuler</Button>
            <Button onClick={handleDeleteProspect} className="bg-red-500 text-white hover:bg-red-600 rounded-full">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CLIENT CREATE DIALOG */}
      <CreateClientDialog open={createDialog} onClose={() => { setCreateDialog(false); setConvertPrefill(null); }} onSuccess={(tp) => { setTempPassword(tp); loadClients(); loadStats(); }} prefill={convertPrefill} />

      {/* CLIENT CREATED DIALOG */}
      <Dialog open={!!tempPassword} onOpenChange={() => setTempPassword(null)}>
        <DialogContent className="bg-white border-[#BFBFBF]">
          <DialogHeader><DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Client cree avec succes</DialogTitle></DialogHeader>
          <div className="w-12 h-12 rounded-full bg-[#3F6B6B]/10 flex items-center justify-center mx-auto mb-2">
            <Check className="w-6 h-6 text-[#3F6B6B]" />
          </div>
          <p className="text-sm text-[#6B6B6B] text-center">Un email de bienvenue contenant un <strong className="text-[#2F4F4F]">lien de connexion securise</strong> a ete envoye au client.</p>
          <p className="text-xs text-[#6B6B6B] text-center mt-1">Le client pourra definir son mot de passe lors de sa premiere connexion.</p>
          <DialogFooter><Button onClick={() => setTempPassword(null)} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW CLIENT DIALOG */}
      {viewDialog && <ViewClientDialog client={viewDialog} onClose={() => setViewDialog(null)} />}

      {/* EDIT CLIENT DIALOG */}
      {editDialog && <EditClientDialog client={editDialog} onClose={() => setEditDialog(null)} onSuccess={() => { setEditDialog(null); loadClients(); }} />}

      {/* SEND EMAIL DIALOG (clients) */}
      {emailDialog && <SendEmailDialog client={emailDialog} onClose={() => setEmailDialog(null)} />}

      {/* SEND EMAIL DIALOG (prospects) */}
      {emailProspectDialog && <SendEmailProspectDialog prospect={emailProspectDialog} onClose={() => setEmailProspectDialog(null)} />}

      {/* DELETE CLIENT DIALOG */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="bg-white border-[#BFBFBF]">
          <DialogHeader><DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Supprimer le client</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B6B6B]">Êtes-vous sûr de vouloir supprimer <strong>{deleteDialog?.prenom} {deleteDialog?.nom}</strong> ({deleteDialog?.denomination_sociale}) ?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="border-[#BFBFBF] rounded-full">Annuler</Button>
            <Button data-testid="confirm-delete-client" onClick={handleDeleteClient} className="bg-red-500 text-white hover:bg-red-600 rounded-full">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REJECT PLAN CHANGE DIALOG */}
      <Dialog open={!!rejectDialog} onOpenChange={() => { setRejectDialog(null); setRejectReason(""); }}>
        <DialogContent className="bg-white border-[#BFBFBF]">
          <DialogHeader>
            <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Refuser la demande</DialogTitle>
            <DialogDescription className="text-[#6B6B6B]">Le client {rejectDialog?.user_name} sera informé par email du refus.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Motif (optionnel — sera lu par le client)</Label>
            <textarea
              data-testid="reject-reason-textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex : Engagement minimum non encore atteint, contactez-moi pour en discuter…"
              className="w-full bg-[#FBF9F5] border border-[#BFBFBF] rounded-lg p-3 text-sm min-h-[100px] focus:outline-none focus:border-[#2F4F4F]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)} className="border-[#BFBFBF] rounded-full">Annuler</Button>
            <Button data-testid="confirm-reject-plan" onClick={handleRejectPlanChange} disabled={actionLoading} className="bg-red-500 text-white hover:bg-red-600 rounded-full">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refuser et notifier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIRECT PLAN CHANGE DIALOG (admin initiates) */}
      <Dialog open={!!planDirectDialog} onOpenChange={() => setPlanDirectDialog(null)}>
        <DialogContent className="bg-white border-[#BFBFBF] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Changer le forfait du client</DialogTitle>
            <DialogDescription className="text-[#6B6B6B]">
              {planDirectDialog?.prenom} {planDirectDialog?.nom} — {planDirectDialog?.denomination_sociale}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2 block">Nouveau forfait</Label>
              <div className="space-y-2">
                {plansCatalog?.plans && Object.entries(plansCatalog.plans).map(([key, p]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`direct-plan-${key}`}
                    onClick={() => { setPlanDirectPlan(key); if (!p.options_allowed) setPlanDirectOptions([]); }}
                    className={`w-full text-left p-3.5 rounded-xl border transition-all ${planDirectPlan === key ? "border-[#2F4F4F] bg-[#2F4F4F]/[0.04]" : "border-[#BFBFBF] hover:border-[#2F4F4F]/40"}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-[#2F4F4F]">{p.name}</p>
                      <p className="text-sm text-[#2F4F4F] font-mono">{p.price}€</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {planDirectPlan && plansCatalog?.plans?.[planDirectPlan]?.options_allowed && (
              <div>
                <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2 block">Options</Label>
                <div className="space-y-2">
                  {plansCatalog?.options && Object.entries(plansCatalog.options).map(([key, o]) => (
                    <label key={key} data-testid={`direct-opt-${key}`} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${planDirectOptions.includes(key) ? "border-[#2F4F4F] bg-[#2F4F4F]/[0.04]" : "border-[#BFBFBF]"}`}>
                      <input
                        type="checkbox"
                        checked={planDirectOptions.includes(key)}
                        onChange={() => setPlanDirectOptions(prev => prev.includes(key) ? prev.filter(o2 => o2 !== key) : [...prev, key])}
                        className="w-4 h-4 accent-[#2F4F4F]"
                      />
                      <div className="flex-1 text-sm text-[#2F4F4F]">{o.name}</div>
                      <span className="text-xs text-[#6B6B6B] font-mono">+{o.price}€</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="bg-[#FBF9F5] border border-[#BFBFBF] rounded-lg p-3.5 text-xs text-[#6B6B6B] leading-relaxed">
              <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5 text-[#2F4F4F]" />
              Conformément à l'<strong>Article 3 bis</strong> des CGV : mise à jour Stripe (proration auto), ancien contrat invalidé, email envoyé au client pour signer le nouveau contrat (engagement initial de 6 mois).
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDirectDialog(null)} className="border-[#BFBFBF] rounded-full">Annuler</Button>
            <Button data-testid="confirm-direct-plan-change" onClick={handleSubmitDirectPlanChange} disabled={actionLoading || !planDirectPlan} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRightLeft className="w-4 h-4 mr-2" /> Exécuter le changement</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
    </TooltipProvider>
  );
}

// ── PROSPECT CREATE DIALOG ──
function ProspectCreateDialog({ open, onClose, onSuccess }) {
  const emptyForm = { nom_entreprise: "", email: "", site_demo: "", google_maps_url: "" };
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "bg-[#FBF9F5] border-[#BFBFBF] mt-1 text-sm";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/prospects", form);
      toast.success("Prospect créé et email de prospection envoyé !");
      setForm(emptyForm);
      onSuccess(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#BFBFBF] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Nouveau prospect</DialogTitle>
          <DialogDescription className="text-[#6B6B6B] text-xs">Un email de prospection sera envoye automatiquement (adapte selon la presence d'un site de demo).</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label className="text-xs text-[#6B6B6B]">Nom de l'entreprise</Label><Input data-testid="prospect-nom-entreprise" value={form.nom_entreprise} onChange={(e) => update("nom_entreprise", e.target.value)} required className={inputCls} placeholder="Boulangerie Dupont" /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Email</Label><Input data-testid="prospect-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className={inputCls} placeholder="contact@boulangerie-dupont.fr" /></div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Lien du site de demonstration <span className="text-[#999] font-normal">(facultatif)</span></Label>
            <Input data-testid="prospect-site-demo" value={form.site_demo} onChange={(e) => update("site_demo", e.target.value)} className={inputCls} placeholder="https://demo.monbeausite.com/boulangerie" />
            <p className="text-[10px] text-[#999] mt-1">{form.site_demo ? "Sequence \"Avec Demo\" : le prospect recevra le lien de son site." : "Sequence \"Sans Demo\" : le prospect sera invite a vous contacter pour decouvrir son futur site."}</p>
          </div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Fiche Google Maps <span className="text-[#999] font-normal">(facultatif)</span></Label>
            <Input data-testid="prospect-gmaps" value={form.google_maps_url} onChange={(e) => update("google_maps_url", e.target.value)} className={inputCls} placeholder="https://www.google.com/maps/place/..." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-[#BFBFBF] rounded-full">Annuler</Button>
            <Button data-testid="prospect-submit" type="submit" disabled={loading} className="bg-[#3F6B6B] text-white hover:bg-[#4A6B51] rounded-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Envoyer</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── PROSPECT EDIT DIALOG ──
function ProspectEditDialog({ prospect, onClose, onSuccess }) {
  const [form, setForm] = useState({
    nom_entreprise: prospect.nom_entreprise || "",
    email: prospect.email || "",
    site_demo: prospect.site_demo || "",
    google_maps_url: prospect.google_maps_url || "",
  });
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "bg-[#FBF9F5] border-[#BFBFBF] mt-1 text-sm";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/admin/prospects/${prospect.id}`, form);
      toast.success("Prospect mis à jour");
      onSuccess();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#BFBFBF] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Modifier le prospect</DialogTitle>
          <DialogDescription className="text-[#6B6B6B] text-xs">{prospect.nom_entreprise}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label className="text-xs text-[#6B6B6B]">Nom de l'entreprise</Label><Input data-testid="edit-prospect-nom-entreprise" value={form.nom_entreprise} onChange={(e) => update("nom_entreprise", e.target.value)} required className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Email</Label><Input data-testid="edit-prospect-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Lien du site de demonstration <span className="text-[#999] font-normal">(facultatif)</span></Label><Input data-testid="edit-prospect-site-demo" value={form.site_demo} onChange={(e) => update("site_demo", e.target.value)} className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Fiche Google Maps <span className="text-[#999] font-normal">(facultatif)</span></Label><Input data-testid="edit-prospect-gmaps" value={form.google_maps_url} onChange={(e) => update("google_maps_url", e.target.value)} className={inputCls} placeholder="https://www.google.com/maps/place/..." /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-[#BFBFBF] rounded-full">Annuler</Button>
            <Button data-testid="edit-prospect-submit" type="submit" disabled={loading} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ── CLIENT CREATE DIALOG ──
function CreateClientDialog({ open, onClose, onSuccess, prefill }) {
  const emptyForm = { email: "", nom: "", prenom: "", denomination_sociale: "", siret: "", email_pro: "", telephone: "", adresse_rue: "", adresse_code_postal: "", adresse_ville: "", site_demo: "", site_web: "", google_maps_url: "", plan: "la_vitrine_dynamique", options: [], sector: "" };
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && prefill) {
      setForm(f => ({
        ...f,
        email: prefill.email || "",
        denomination_sociale: prefill.denomination_sociale || prefill.nom_entreprise || "",
        site_demo: prefill.site_demo || "",
        google_maps_url: prefill.google_maps_url || "",
      }));
    } else if (open && !prefill) {
      setForm(emptyForm);
    }
  }, [open, prefill]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "bg-[#FBF9F5] border-[#BFBFBF] mt-1 text-sm";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/clients", form);
      toast.success("Client créé !");
      onSuccess(data.magic_link || true);
      setForm(emptyForm);
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#BFBFBF] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Nouveau client</DialogTitle>
          <DialogDescription className="text-[#6B6B6B] text-xs">Remplissez les informations du client. Un email de bienvenue sera envoyé.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Prénom</Label><Input data-testid="create-client-prenom" value={form.prenom} onChange={(e) => update("prenom", e.target.value)} required className={inputCls} /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Nom</Label><Input data-testid="create-client-nom" value={form.nom} onChange={(e) => update("nom", e.target.value)} required className={inputCls} /></div>
          </div>
          <div><Label className="text-xs text-[#6B6B6B]">Dénomination sociale</Label><Input data-testid="create-client-denomination" value={form.denomination_sociale} onChange={(e) => update("denomination_sociale", e.target.value)} required className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">SIRET</Label><Input data-testid="create-client-siret" value={form.siret} onChange={(e) => update("siret", e.target.value)} required className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Email (identifiant de connexion)</Label><Input data-testid="create-client-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Email professionnel</Label><Input data-testid="create-client-email-pro" type="email" value={form.email_pro} onChange={(e) => update("email_pro", e.target.value)} placeholder="contact@entreprise.fr" className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Téléphone</Label><Input data-testid="create-client-telephone" value={form.telephone} onChange={(e) => update("telephone", e.target.value)} required className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Adresse (rue et numero) *</Label><Input data-testid="create-client-adresse-rue" value={form.adresse_rue} onChange={(e) => update("adresse_rue", e.target.value)} required className={inputCls} placeholder="12 rue du Commerce" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Code postal *</Label><Input data-testid="create-client-adresse-cp" value={form.adresse_code_postal} onChange={(e) => update("adresse_code_postal", e.target.value)} required className={inputCls} placeholder="26200" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Ville *</Label><Input data-testid="create-client-adresse-ville" value={form.adresse_ville} onChange={(e) => update("adresse_ville", e.target.value)} required className={inputCls} placeholder="Montelimar" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Site web de demonstration</Label><Input data-testid="create-client-site-demo" value={form.site_demo} onChange={(e) => update("site_demo", e.target.value)} placeholder="https://demo..." className={inputCls} /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Site web</Label><Input data-testid="create-client-site-web" value={form.site_web} onChange={(e) => update("site_web", e.target.value)} placeholder="https://www..." className={inputCls} /></div>
          </div>
          <div><Label className="text-xs text-[#6B6B6B]">Fiche Google Maps <span className="text-[#999] font-normal">(facultatif)</span></Label><Input data-testid="create-client-gmaps" value={form.google_maps_url} onChange={(e) => update("google_maps_url", e.target.value)} placeholder="https://www.google.com/maps/place/..." className={inputCls} /></div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Secteur d'activité</Label>
            <Select value={form.sector || "none"} onValueChange={(v) => update("sector", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="create-client-sector" className="bg-[#FBF9F5] border-[#BFBFBF] mt-1"><SelectValue placeholder="Choisir un secteur" /></SelectTrigger>
              <SelectContent className="bg-white border-[#BFBFBF] max-h-72">
                <SelectItem value="none">— Non renseigné —</SelectItem>
                {Object.entries(getGroupedSectors()).map(([group, sectors]) => (
                  <div key={group}>
                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-[#C46A4A] font-semibold border-t border-[#BFBFBF]/30 first:border-t-0 bg-[#FBF9F5]">{group}</div>
                    {sectors.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Forfait</Label>
            <Select value={form.plan} onValueChange={(v) => update("plan", v)}>
              <SelectTrigger data-testid="create-client-plan" className="bg-[#FBF9F5] border-[#BFBFBF] mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#BFBFBF]">
                <SelectItem value="la_vitrine">La Vitrine Active - 39 EUR/mois</SelectItem>
                <SelectItem value="la_vitrine_dynamique">La Formule Croissance - 59 EUR/mois</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-[#BFBFBF] rounded-full">Annuler</Button>
            <Button data-testid="create-client-submit" type="submit" disabled={loading} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer le client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── VIEW CLIENT DIALOG ──
function BrandGuideDialog({ open, onClose }) {
  const today = new Date().toISOString().split("T")[0].split("-").reverse().join("/");
  const [form, setForm] = useState({
    client_name: "",
    delivery_date: today,
    color_primary: "#1F3535",
    color_primary_name: "Couleur primaire",
    color_accent: "#C46A4A",
    color_accent_name: "Couleur accent",
    color_secondary: "#D4AF37",
    color_secondary_name: "Couleur secondaire",
    color_neutral: "#FBF9F5",
    color_neutral_name: "Neutre / fond",
    font_title: "",
    font_subtitle: "",
    font_body: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleGenerate = async () => {
    if (!form.client_name.trim()) { toast.error("Nom du client requis"); return; }
    setLoading(true);
    try {
      const response = await api.post("/admin/brand-guide", form, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safe = form.client_name.replace(/[^a-zA-Z0-9-_]/g, "_");
      link.download = `guide-identite-${safe}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Guide téléchargé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de la génération");
    } finally {
      setLoading(false);
    }
  };

  const isValidHex = (v) => /^#[0-9A-F]{6}$/i.test(v || "");

  const handleHexChange = (hexKey, raw) => {
    // Auto-prefix with '#', keep only hex chars, cap at 6, force uppercase
    const cleaned = raw.trim().replace(/^#/, "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    update(hexKey, cleaned ? "#" + cleaned.toUpperCase() : "");
  };

  const renderColorField = (label, hexKey, nameKey) => {
    // The native <input type="color"> requires strictly '#rrggbb' in lowercase.
    // We keep an uppercase display in the text field for readability,
    // but feed a validated lowercase value to the picker so it stays in sync.
    const valid = isValidHex(form[hexKey]);
    const pickerValue = valid ? form[hexKey].toLowerCase() : "#000000";
    return (
      <div className="space-y-1.5" key={hexKey}>
        <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">{label}</Label>
        <div className="flex gap-2">
          <input
            type="color"
            data-testid={`bg-color-${hexKey}`}
            value={pickerValue}
            onChange={e => update(hexKey, e.target.value.toUpperCase())}
            className="w-12 h-9 rounded border border-[#BFBFBF] cursor-pointer flex-shrink-0"
          />
          <Input
            data-testid={`bg-hex-${hexKey}`}
            value={form[hexKey]}
            onChange={e => handleHexChange(hexKey, e.target.value)}
            placeholder="#000000"
            maxLength={7}
            className={`w-24 font-mono text-xs bg-[#FBF9F5] ${!valid && form[hexKey] ? "border-orange-400" : "border-[#BFBFBF]"}`}
          />
          <Input
            value={form[nameKey]}
            onChange={e => update(nameKey, e.target.value)}
            placeholder="Nom couleur"
            className="flex-1 text-xs bg-[#FBF9F5] border-[#BFBFBF]"
          />
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#BFBFBF] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F] flex items-center gap-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            <Palette className="w-5 h-5 text-[#C46A4A]" /> Générer un guide d'identité visuelle
          </DialogTitle>
          <DialogDescription className="text-[#6B6B6B] text-xs">
            Personnalisez le guide PDF pour un client. Vous le téléchargez puis vous l'envoyez par email vous-même.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Nom du client / marque *</Label>
              <Input data-testid="bg-client-name" value={form.client_name} onChange={e => update("client_name", e.target.value)} placeholder="Le Privé Coiffeur" className="mt-1 bg-[#FBF9F5] border-[#BFBFBF]" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Date de livraison</Label>
              <Input data-testid="bg-delivery-date" value={form.delivery_date} onChange={e => update("delivery_date", e.target.value)} placeholder="jj/mm/aaaa" className="mt-1 bg-[#FBF9F5] border-[#BFBFBF]" />
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-[#2F4F4F] uppercase tracking-wider mb-3">Palette de couleurs</h4>
            <div className="space-y-2.5">
              {renderColorField("Couleur primaire", "color_primary", "color_primary_name")}
              {renderColorField("Couleur accent", "color_accent", "color_accent_name")}
              {renderColorField("Couleur secondaire", "color_secondary", "color_secondary_name")}
              {renderColorField("Neutre / fond", "color_neutral", "color_neutral_name")}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-[#2F4F4F] uppercase tracking-wider mb-3">Typographies</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Titre (H1)</Label>
                <Input data-testid="bg-font-title" value={form.font_title} onChange={e => update("font_title", e.target.value)} placeholder="Playfair Display" className="mt-1 bg-[#FBF9F5] border-[#BFBFBF]" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Sous-titre (H2)</Label>
                <Input data-testid="bg-font-subtitle" value={form.font_subtitle} onChange={e => update("font_subtitle", e.target.value)} placeholder="Montserrat Bold" className="mt-1 bg-[#FBF9F5] border-[#BFBFBF]" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Texte</Label>
                <Input data-testid="bg-font-body" value={form.font_body} onChange={e => update("font_body", e.target.value)} placeholder="Inter" className="mt-1 bg-[#FBF9F5] border-[#BFBFBF]" />
              </div>
            </div>
            <p className="text-[10px] text-[#6B6B6B] mt-2 italic">Note : les noms des typos apparaîtront dans le PDF, mais le rendu utilise Helvetica (limite technique FPDF).</p>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-3 border-t border-[#BFBFBF]">
          <Button variant="outline" onClick={onClose} className="border-[#BFBFBF] rounded-full">Annuler</Button>
          <Button
            data-testid="bg-generate-btn"
            onClick={handleGenerate}
            disabled={loading}
            className="bg-[#C46A4A] text-white hover:bg-[#A6553A] rounded-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Générer et télécharger</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewClientDialog({ client, onClose }) {
  const [archives, setArchives] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setArchivesLoading(true);
      try {
        const { data } = await api.get(`/admin/clients/${client.id}/contract-archives`);
        if (!cancelled) setArchives(data.archives || []);
      } catch (err) { console.warn("Could not load contract archives:", err?.message); } finally {
        if (!cancelled) setArchivesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client.id]);

  const handleDownloadContract = async () => {
    try {
      const response = await api.get(`/admin/clients/${client.id}/contract-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrat-monbeausite-${(client.denomination_sociale || 'client').replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du téléchargement du contrat");
    }
  };

  const handleDownloadArchive = async (archive) => {
    try {
      const response = await api.get(`/admin/clients/${client.id}/contract-archives/${archive.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const dateStr = (archive.signed_at || "").slice(0, 10);
      const planLabel = (PLANS[archive.plan] || archive.plan).replace(/\s/g, "_");
      link.download = `contrat-archive-${planLabel}-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du téléchargement du contrat archivé");
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#BFBFBF] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{client.prenom} {client.nom}</DialogTitle>
          <DialogDescription className="text-[#6B6B6B] text-xs">{client.denomination_sociale}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {[
            ["Email de contact", client.email], ["Email pro", client.email_pro], ["SIRET", client.siret], ["Téléphone", client.telephone],
            ["Adresse", `${client.adresse_rue || ""} ${client.adresse_code_postal || ""} ${client.adresse_ville || ""}`.trim()],
            ["Site demo", client.site_demo], ["Site web", client.site_web],
            ["Secteur d'activité", client.sector ? getSectorLabel(client.sector) : ""],
            ["Forfait", PLANS[client.plan] || client.plan],
            ["Options", client.options?.map(o => OPTIONS_MAP[o] || o).join(", ") || "Aucune"],
            ["Contrat", client.contract_signed ? "Signé" : "En attente"],
            ["Créé le", new Date(client.created_at).toLocaleDateString("fr-FR")],
          ].map(([label, value], i) => (
            <div key={i} className="flex justify-between items-start border-b border-[#BFBFBF] pb-2">
              <span className="text-[#6B6B6B]">{label}</span>
              <span className="text-right font-medium max-w-[60%] text-[#2F4F4F]">
                {label.startsWith("Site") && value ? <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#2F4F4F] underline">{value}</a> : (value || "-")}
              </span>
            </div>
          ))}
        </div>
        {client.contract_signed && (
          <div className="pt-3 border-t border-[#BFBFBF]">
            <Button
              data-testid="admin-download-contract-btn"
              onClick={handleDownloadContract}
              className="w-full bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full text-xs"
            >
              <FileText className="w-3.5 h-3.5 mr-2" /> Télécharger le contrat actuel signé (PDF)
            </Button>
          </div>
        )}

        {/* Contract archives — anciens contrats signés (avant changement de plan) */}
        <div className="pt-3 border-t border-[#BFBFBF]">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-[#2F4F4F] uppercase tracking-wider flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-[#C46A4A]" /> Contrats archivés
            </h4>
            {!archivesLoading && <span className="text-[10px] text-[#6B6B6B]">{archives.length} version{archives.length > 1 ? "s" : ""}</span>}
          </div>
          {archivesLoading ? (
            <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[#6B6B6B]" /></div>
          ) : archives.length === 0 ? (
            <p className="text-xs text-[#6B6B6B] italic py-2">Aucun contrat archivé. Les anciens contrats apparaîtront ici lors d'un changement de forfait.</p>
          ) : (
            <div className="space-y-2">
              {archives.map((a) => (
                <div key={a.id} data-testid={`archive-${a.id}`} className="flex items-center justify-between bg-[#FBF9F5] border border-[#BFBFBF] rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#2F4F4F]">
                      {PLANS[a.plan] || a.plan}
                      {a.options?.length > 0 && <span className="text-[#6B6B6B] font-normal"> + {a.options.map(o => OPTIONS_MAP[o] || o).join(", ")}</span>}
                    </p>
                    <p className="text-[10px] text-[#6B6B6B] mt-0.5">
                      Signé le {a.signed_at ? new Date(a.signed_at).toLocaleDateString("fr-FR") : "—"}
                      {" • "}archivé le {a.archived_at ? new Date(a.archived_at).toLocaleDateString("fr-FR") : "—"}
                      {a.signed_hash && <span title={`SHA-256: ${a.signed_hash}`}> • #{a.signed_hash.slice(0, 8)}</span>}
                    </p>
                  </div>
                  <Button
                    data-testid={`download-archive-${a.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadArchive(a)}
                    className="border-[#BFBFBF] text-[#2F4F4F] hover:bg-[#2F4F4F]/5 rounded-full text-[10px] h-7 px-3 ml-2 flex-shrink-0"
                  >
                    <FileText className="w-3 h-3 mr-1" /> PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── EDIT CLIENT DIALOG ──
function EditClientDialog({ client, onClose, onSuccess }) {
  const [form, setForm] = useState({ nom: client.nom || "", prenom: client.prenom || "", email: client.email || "", denomination_sociale: client.denomination_sociale || "", siret: client.siret || "", email_pro: client.email_pro || "", telephone: client.telephone || "", adresse_rue: client.adresse_rue || "", adresse_code_postal: client.adresse_code_postal || "", adresse_ville: client.adresse_ville || "", site_demo: client.site_demo || "", site_web: client.site_web || "", google_maps_url: client.google_maps_url || "", plan: client.plan || "la_vitrine", options: client.options || [], sector: client.sector || "" });
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "bg-[#FBF9F5] border-[#BFBFBF] mt-1 text-sm";

  const toggleOption = (optKey) => {
    setForm(f => ({
      ...f,
      options: f.options.includes(optKey) ? f.options.filter(o => o !== optKey) : [...f.options, optKey]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.put(`/admin/clients/${client.id}`, form); toast.success("Client mis à jour"); onSuccess(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#BFBFBF] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Modifier {client.prenom} {client.nom}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Prénom</Label><Input value={form.prenom} onChange={(e) => update("prenom", e.target.value)} className={inputCls} /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Nom</Label><Input value={form.nom} onChange={(e) => update("nom", e.target.value)} className={inputCls} /></div>
          </div>
          <div><Label className="text-xs text-[#6B6B6B]">Email de contact (connexion & prospection)</Label><Input value={form.email} onChange={(e) => update("email", e.target.value)} type="email" className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Dénomination sociale</Label><Input value={form.denomination_sociale} onChange={(e) => update("denomination_sociale", e.target.value)} className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">SIRET</Label><Input value={form.siret} onChange={(e) => update("siret", e.target.value)} className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Email professionnel</Label><Input value={form.email_pro} onChange={(e) => update("email_pro", e.target.value)} className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Téléphone</Label><Input value={form.telephone} onChange={(e) => update("telephone", e.target.value)} className={inputCls} /></div>
          <div><Label className="text-xs text-[#6B6B6B]">Adresse (rue et numero)</Label><Input value={form.adresse_rue} onChange={(e) => update("adresse_rue", e.target.value)} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Code postal</Label><Input value={form.adresse_code_postal} onChange={(e) => update("adresse_code_postal", e.target.value)} className={inputCls} /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Ville</Label><Input value={form.adresse_ville} onChange={(e) => update("adresse_ville", e.target.value)} className={inputCls} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Site de démonstration</Label><Input value={form.site_demo} onChange={(e) => update("site_demo", e.target.value)} placeholder="https://demo..." className={inputCls} /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Site web</Label><Input value={form.site_web} onChange={(e) => update("site_web", e.target.value)} placeholder="https://www..." className={inputCls} /></div>
          </div>
          <div><Label className="text-xs text-[#6B6B6B]">Fiche Google Maps</Label><Input value={form.google_maps_url} onChange={(e) => update("google_maps_url", e.target.value)} placeholder="https://www.google.com/maps/place/..." className={inputCls} /></div>

          {/* Secteur d'activité */}
          <div>
            <Label className="text-xs text-[#6B6B6B] font-semibold">Secteur d'activité</Label>
            <Select value={form.sector || "none"} onValueChange={(v) => update("sector", v === "none" ? "" : v)}>
              <SelectTrigger className="bg-[#FBF9F5] border-[#BFBFBF] mt-1 text-sm" data-testid="edit-client-sector">
                <SelectValue placeholder="Choisir un secteur" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#BFBFBF] max-h-72">
                <SelectItem value="none">— Non renseigné —</SelectItem>
                {Object.entries(getGroupedSectors()).map(([group, sectors]) => (
                  <div key={group}>
                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-[#C46A4A] font-semibold border-t border-[#BFBFBF]/30 first:border-t-0 bg-[#FBF9F5]">{group}</div>
                    {sectors.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Forfait */}
          <div className="border-t border-[#BFBFBF] pt-3 mt-3">
            <Label className="text-xs text-[#6B6B6B] font-semibold">Forfait</Label>
            <Select value={form.plan} onValueChange={(v) => update("plan", v)}>
              <SelectTrigger className="bg-[#FBF9F5] border-[#BFBFBF] mt-1 text-sm" data-testid="edit-client-plan-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="la_vitrine">La Vitrine Active — 39 EUR/mois</SelectItem>
                <SelectItem value="la_vitrine_dynamique">La Formule Croissance — 59 EUR/mois</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Options */}
          <div>
            <Label className="text-xs text-[#6B6B6B] font-semibold">Options</Label>
            {form.plan !== "la_vitrine_dynamique" && (
              <p className="text-xs text-[#3F6B6B] italic mt-1">Les options sont réservées au forfait Formule Croissance.</p>
            )}
            <div className="space-y-2 mt-2">
              {Object.entries(OPTIONS_MAP).map(([key, label]) => (
                <label key={key} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  form.options.includes(key) ? "border-[#2F4F4F]/30 bg-[#2F4F4F]/5" : "border-[#BFBFBF] bg-[#FBF9F5] hover:border-[#3F6B6B]/30"
                } ${form.plan !== "la_vitrine_dynamique" ? "opacity-50 pointer-events-none" : ""}`}>
                  <input
                    type="checkbox"
                    checked={form.options.includes(key)}
                    onChange={() => toggleOption(key)}
                    disabled={form.plan !== "la_vitrine_dynamique"}
                    className="w-4 h-4 rounded border-[#BFBFBF] text-[#2F4F4F] focus:ring-[#2F4F4F]"
                    data-testid={`edit-client-option-${key}`}
                  />
                  <span className="text-sm text-[#2F4F4F]">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-[#BFBFBF] rounded-full">Annuler</Button>
            <Button data-testid="edit-client-submit" type="submit" disabled={loading} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ── CONTRACT EDITOR ──
function ContractEditor() {
  const [template, setTemplate] = useState("");
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contract-template");
      setTemplate(data.template);
      setSource(data.source);
    } catch (err) { toast.error("Erreur lors du chargement du contrat"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/contract-template", { template });
      toast.success("Template de contrat enregistré");
      setSource("custom");
      setEditing(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm("Remettre le contrat par défaut ? Vos modifications seront perdues.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/contract-template/reset");
      setTemplate(data.template);
      setSource("default");
      setEditing(false);
      toast.success("Contrat réinitialisé");
    } catch (err) { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#2F4F4F]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Template de contrat</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">
            {source === "custom" ? "Version personnalisée" : "Version par défaut"} — Ce texte sera présenté aux clients lors de leur première connexion.
          </p>
        </div>
        <div className="flex gap-2">
          {!editing ? (
            <Button data-testid="contract-edit-btn" onClick={() => setEditing(true)} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full text-xs">
              <Edit2 className="w-3.5 h-3.5 mr-2" /> Modifier
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setEditing(false); loadTemplate(); }} className="border-[#BFBFBF] rounded-full text-xs">Annuler</Button>
              {source === "custom" && (
                <Button variant="outline" onClick={handleReset} className="border-[#BFBFBF] text-[#6B6B6B] rounded-full text-xs">
                  <RefreshCw className="w-3.5 h-3.5 mr-2" /> Défaut
                </Button>
              )}
              <Button data-testid="contract-save-btn" onClick={handleSave} disabled={saving} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full text-xs">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-2" /> Enregistrer</>}
              </Button>
            </>
          )}
        </div>
      </div>

      {editing && (
        <div className="bg-white border border-[#BFBFBF] rounded-xl p-4">
          <p className="text-xs text-[#3F6B6B] mb-3 font-medium">Variables disponibles (insérées automatiquement pour chaque client) :</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              "{contract_id}", "{client_prenom}", "{client_nom}", "{denomination_sociale}",
              "{client_siret}", "{adresse_facturation}", "{client_email}", "{client_telephone}",
              "{plan_name}", "{plan_price}", "{options_text}", "{total_mensuel}",
              "{engagement_months}", "{date_debut}", "{date_fin}", "{date_signature}",
            ].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => { navigator.clipboard.writeText(v); toast.success(`${v} copié`); }}
                className="text-xs bg-[#2F4F4F]/5 border border-[#2F4F4F]/15 text-[#2F4F4F] px-2 py-1 rounded-md hover:bg-[#2F4F4F]/10 transition-colors cursor-pointer font-mono"
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#BFBFBF] rounded-xl overflow-hidden">
        {editing ? (
          <textarea
            data-testid="contract-textarea"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className="w-full h-[600px] p-6 text-sm text-[#333333] leading-relaxed resize-none focus:outline-none font-mono bg-white"
            style={{ fontFamily: "'Courier New', monospace" }}
          />
        ) : (
          <ScrollArea className="h-[600px]">
            <pre className="p-6 text-sm text-[#333333] leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {template}
            </pre>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}

// ── SEND EMAIL DIALOG ──
function SendEmailDialog({ client, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const canSend = subject.trim().length > 0 && body.trim().length > 0;

  const handleSend = async () => {
    if (!canSend) return;
    setLoading(true);
    try {
      await api.post(`/admin/clients/${client.id}/send-email`, { subject: subject.trim(), body: body.trim() });
      setSent(true);
      toast.success(`Email envoyé à ${client.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi");
    } finally { setLoading(false); }
  };

  const handleClose = () => {
    if (!sent && (subject.trim() || body.trim())) {
      if (!window.confirm("Votre message n'a pas été envoyé. Fermer quand même ?")) return;
    }
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="bg-white border-[#BFBFBF] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {sent ? "Email envoyé" : "Envoyer un email"}
          </DialogTitle>
          <DialogDescription className="text-[#6B6B6B] text-xs">
            {sent
              ? `Votre message a été envoyé à ${client.prenom} ${client.nom}.`
              : `Envoyer un email à ${client.prenom} ${client.nom} — cet email utilisera le template MonBeauSite.`
            }
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#3F6B6B]/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-[#3F6B6B]" />
            </div>
            <p className="text-sm text-[#333333] mb-1">Message envoyé avec succès</p>
            <p className="text-xs text-[#6B6B6B]">{client.email}</p>
            <Button onClick={onClose} className="mt-6 bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full px-8">Fermer</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Destinataire (non éditable) */}
            <div>
              <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Destinataire</Label>
              <div className="mt-1 flex items-center gap-2 bg-[#FBF9F5] border border-[#BFBFBF] rounded-md px-3 py-2.5">
                <Mail className="w-3.5 h-3.5 text-[#6B6B6B] flex-shrink-0" />
                <span className="text-sm text-[#2F4F4F] font-medium">{client.email}</span>
                <span className="text-xs text-[#6B6B6B] ml-auto">{client.prenom} {client.nom}</span>
              </div>
            </div>

            {/* Objet */}
            <div>
              <Label htmlFor="email-subject" className="text-xs text-[#6B6B6B] uppercase tracking-wider">Objet *</Label>
              <Input
                id="email-subject"
                data-testid="send-email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Ex : Informations sur votre site web"
                className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] text-sm focus:ring-[#2F4F4F]/20 focus:border-[#2F4F4F]/40"
                autoFocus
              />
              {subject.length > 0 && subject.trim().length === 0 && (
                <p className="text-xs text-red-500 mt-1">L'objet ne peut pas être vide.</p>
              )}
            </div>

            {/* Corps du message */}
            <div>
              <Label htmlFor="email-body" className="text-xs text-[#6B6B6B] uppercase tracking-wider">Message *</Label>
              <textarea
                id="email-body"
                data-testid="send-email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Rédigez votre message ici..."
                rows={8}
                className="mt-1 w-full bg-[#FBF9F5] border border-[#BFBFBF] rounded-md px-3 py-2.5 text-sm text-[#333333] placeholder:text-[#999] resize-none focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/20 focus:border-[#2F4F4F]/40 leading-relaxed"
              />
              <p className="text-[10px] text-[#999] mt-1">
                Signature ajoutée automatiquement : <em>« Bonne journée, Émilien — Fondateur de www.monbeausite.com — 06 52 15 10 96 »</em>
              </p>
            </div>

            <DialogFooter className="flex gap-2 pt-2">
              <Button variant="outline" onClick={handleClose} className="border-[#BFBFBF] text-[#6B6B6B] hover:text-[#2F4F4F] rounded-full">
                Annuler
              </Button>
              <Button
                data-testid="send-email-submit"
                onClick={handleSend}
                disabled={!canSend || loading}
                className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Envoi..." : "Envoyer"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


const PLAN_OPTIONS = [
  { value: "all_plans", label: "Tous les forfaits" },
  { value: "la_vitrine", label: "Vitrine Active" },
  { value: "la_vitrine_dynamique", label: "Formule Croissance" },
];
const OPT_OPTIONS = [
  { value: "all_options", label: "Toutes les options" },
  { value: "fidelite_marketing_plus", label: "Fidelite & Marketing +" },
  { value: "visibilite_seo", label: "Visibilite SEO" },
];
const TARGET_OPTIONS = [
  { value: "all", label: "Tous (clients + prospects + opt-in)" },
  { value: "clients", label: "Clients actifs uniquement" },
  { value: "prospects", label: "Prospects uniquement" },
  { value: "optin", label: "Opt-in marketing uniquement" },
];

function NewsletterTool() {
  const [target, setTarget] = useState("all");
  const [planFilter, setPlanFilter] = useState("all_plans");
  const [optionFilter, setOptionFilter] = useState("all_options");
  const [sectorFilter, setSectorFilter] = useState("all_sectors");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const loadRecipients = useCallback(async () => {
    setRecipientsLoading(true);
    try {
      const params = new URLSearchParams({ target });
      if (planFilter && planFilter !== "all_plans") params.append("plan_filter", planFilter);
      if (optionFilter && optionFilter !== "all_options") params.append("option_filter", optionFilter);
      if (sectorFilter && sectorFilter !== "all_sectors") params.append("sector_filter", sectorFilter);
      const { data } = await api.get(`/admin/newsletter/recipients?${params}`);
      setRecipients(data.recipients || []);
    } catch (err) { console.warn("loadRecipients failed:", err?.message); } finally { setRecipientsLoading(false); }
  }, [target, planFilter, optionFilter, sectorFilter]);

  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/admin/newsletter/history"); setHistory(data.campaigns || []); }
    catch (err) { console.warn("loadHistory failed:", err?.message); }
  }, []);

  useEffect(() => { loadRecipients(); }, [loadRecipients]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("L'objet et le message sont requis."); return; }
    if (recipients.length === 0) { toast.error("Aucun destinataire."); return; }
    if (!window.confirm(`Envoyer cette newsletter a ${recipients.length} destinataire(s) ?`)) return;
    setSending(true);
    try {
      const pf = planFilter !== "all_plans" ? planFilter : null;
      const of_ = optionFilter !== "all_options" ? optionFilter : null;
      const sf = sectorFilter !== "all_sectors" ? sectorFilter : null;
      const { data } = await api.post("/admin/newsletter/send", { subject, body, target, plan_filter: pf, option_filter: of_, sector_filter: sf });
      setSent(data);
      toast.success(`Newsletter envoyee a ${data.sent} destinataire(s)`);
      loadHistory();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur d'envoi"); }
    finally { setSending(false); }
  };

  const resetForm = () => { setSubject(""); setBody(""); setSent(null); };
  const sourceLabel = (s) => s === "client" ? "Client" : s === "prospect" ? "Prospect" : "Opt-in";
  const sourceBadge = (s) => s === "client" ? "bg-[#2F4F4F]/10 text-[#2F4F4F] border-[#2F4F4F]/20" : s === "prospect" ? "bg-[#3F6B6B]/10 text-[#3F6B6B] border-[#3F6B6B]/20" : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Newsletter & Broadcasting</h3>
          <p className="text-sm text-[#6B6B6B] mt-1">Envoyez des communications ciblees a vos clients et prospects.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} className="border-[#BFBFBF] text-[#6B6B6B] hover:text-[#2F4F4F] rounded-full text-xs">
          <Clock className="w-3.5 h-3.5 mr-1.5" /> Historique
        </Button>
      </div>

      {showHistory && history.length > 0 && (
        <div className="bg-white border border-[#BFBFBF] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#BFBFBF] bg-[#FBF9F5]">
            <p className="text-xs text-[#6B6B6B] font-medium uppercase tracking-wider">Dernieres campagnes</p>
          </div>
          <div className="divide-y divide-[#BFBFBF]">
            {history.slice(0, 5).map((h, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2F4F4F]">{h.subject}</p>
                  <p className="text-xs text-[#6B6B6B] mt-0.5">{new Date(h.sent_at).toLocaleString("fr-FR")} — {h.target}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-[#2F4F4F]">{h.sent_count}/{h.total_recipients}</p>
                  <p className="text-xs text-[#6B6B6B]">envoyes</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sent ? (
        <div className="bg-white border border-[#2F4F4F]/20 rounded-xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#3F6B6B]/10 flex items-center justify-center mx-auto mb-5">
            <Check className="w-7 h-7 text-[#3F6B6B]" />
          </div>
          <h3 className="text-lg font-semibold text-[#2F4F4F] mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Newsletter envoyee !</h3>
          <p className="text-sm text-[#6B6B6B] mb-1">{sent.sent} email(s) envoye(s) sur {sent.total} destinataire(s).</p>
          {sent.errors > 0 && <p className="text-xs text-red-500">{sent.errors} erreur(s) d'envoi.</p>}
          <Button onClick={resetForm} className="mt-6 bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">Nouvelle newsletter</Button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: compose */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-[#BFBFBF] rounded-xl p-5 space-y-4">
              <p className="text-xs text-[#3F6B6B] font-medium uppercase tracking-wider">Ciblage</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#6B6B6B]">Audience</Label>
                  <Select value={target} onValueChange={setTarget}>
                    <SelectTrigger className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{TARGET_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {target !== "prospects" && target !== "optin" && (
                  <>
                    <div>
                      <Label className="text-xs text-[#6B6B6B]">Forfait</Label>
                      <Select value={planFilter} onValueChange={setPlanFilter}>
                        <SelectTrigger className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{PLAN_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-[#6B6B6B]">Option</Label>
                      <Select value={optionFilter} onValueChange={setOptionFilter}>
                        <SelectTrigger className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{OPT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs text-[#6B6B6B]">Secteur d'activité</Label>
                      <Select value={sectorFilter} onValueChange={setSectorFilter}>
                        <SelectTrigger data-testid="newsletter-sector-filter" className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="all_sectors">Tous les secteurs</SelectItem>
                          {Object.entries(getGroupedSectors()).map(([group, sectors]) => (
                            <div key={group}>
                              <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-[#C46A4A] font-semibold border-t border-[#BFBFBF]/30 first:border-t-0 bg-[#FBF9F5]">{group}</div>
                              {sectors.map((s) => (
                                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                      {sectorFilter !== "all_sectors" && (target === "all" || target === "prospects" || target === "optin") && (
                        <p className="text-[11px] text-[#C46A4A] italic mt-1.5">⚠ Le filtre par secteur ne s'applique qu'aux <strong>clients actifs</strong>. Prospects et opt-in sont exclus tant qu'un secteur est sélectionné.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white border border-[#BFBFBF] rounded-xl p-5 space-y-4">
              <p className="text-xs text-[#3F6B6B] font-medium uppercase tracking-wider">Redaction</p>
              <div>
                <Label className="text-xs text-[#6B6B6B]">Objet *</Label>
                <Input data-testid="newsletter-subject" value={subject} onChange={e => setSubject(e.target.value)}
                  placeholder="Ex : Nouvelle fonctionnalite disponible !" className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] text-sm" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6B6B]">Message *</Label>
                <textarea data-testid="newsletter-body" value={body} onChange={e => setBody(e.target.value)}
                  placeholder="Redigez votre message ici...&#10;&#10;Le texte sera mis en forme avec le template MonBeauSite.&#10;Les retours a la ligne sont respectes."
                  rows={12}
                  className="mt-1 w-full bg-[#FBF9F5] border border-[#BFBFBF] rounded-md px-3 py-2.5 text-sm text-[#333333] placeholder:text-[#999] resize-none focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/20 focus:border-[#2F4F4F]/40 leading-relaxed" />
                <p className="text-[10px] text-[#999] mt-1">La signature "Emilien — Fondateur de MonBeauSite" et un lien de desinscription seront ajoutes automatiquement.</p>
              </div>
              <div className="flex items-center justify-between pt-2">
                <Button variant="outline" onClick={() => setShowPreview(!showPreview)} className="border-[#BFBFBF] text-[#6B6B6B] hover:text-[#2F4F4F] rounded-full text-xs">
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> {showPreview ? "Masquer" : "Apercu"}
                </Button>
                <Button data-testid="newsletter-send" onClick={handleSend} disabled={sending || !subject.trim() || !body.trim() || recipients.length === 0}
                  className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full gap-2">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? "Envoi en cours..." : `Envoyer a ${recipients.length} destinataire(s)`}
                </Button>
              </div>
            </div>

            {showPreview && body.trim() && (
              <div className="bg-white border border-[#2F4F4F]/20 rounded-xl p-5">
                <p className="text-xs text-[#3F6B6B] font-medium uppercase tracking-wider mb-3">Apercu du message</p>
                <div className="bg-[#FBF9F5] rounded-lg p-6 border border-[#BFBFBF]">
                  <p className="text-xs text-[#999] mb-2">Objet : <strong className="text-[#2F4F4F]">{subject || "(vide)"}</strong></p>
                  <hr className="border-[#BFBFBF] my-3" />
                  <div className="text-sm text-[#333333] leading-relaxed whitespace-pre-line">{body}</div>
                  <hr className="border-[#BFBFBF] my-3" />
                  <p className="text-xs text-[#6B6B6B]">A bientot,<br /><strong className="text-[#2F4F4F]">Emilien</strong><br /><span className="text-[10px] text-[#999]">Fondateur de MonBeauSite</span></p>
                  <p className="text-[10px] text-[#BBBBBB] mt-3 italic">Se desinscrire de nos communications</p>
                </div>
              </div>
            )}
          </div>

          {/* Right: recipients */}
          <div className="space-y-4">
            <div className="bg-white border border-[#BFBFBF] rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-[#BFBFBF] bg-[#FBF9F5] flex items-center justify-between">
                <p className="text-xs text-[#6B6B6B] font-medium uppercase tracking-wider">Destinataires</p>
                <span className="text-xs font-semibold text-[#2F4F4F]">{recipientsLoading ? "..." : recipients.length}</span>
              </div>
              <ScrollArea className="h-[400px]">
                {recipientsLoading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#2F4F4F]" /></div>
                ) : recipients.length === 0 ? (
                  <p className="text-center text-[#6B6B6B]/50 py-12 text-sm">Aucun destinataire</p>
                ) : (
                  <div className="divide-y divide-[#BFBFBF]">
                    {recipients.map((r, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-[#333333] truncate">{r.email}</p>
                          <p className="text-xs text-[#6B6B6B] truncate">{r.prenom} {r.nom}</p>
                        </div>
                        <Badge className={`text-[10px] ml-2 flex-shrink-0 ${sourceBadge(r.source)}`}>{sourceLabel(r.source)}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SEND EMAIL PROSPECT DIALOG ──
function SendEmailProspectDialog({ prospect, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const canSend = subject.trim().length > 0 && body.trim().length > 0;

  const handleSend = async () => {
    if (!canSend) return;
    setLoading(true);
    try {
      await api.post(`/admin/prospects/${prospect.id}/send-email`, { subject: subject.trim(), body: body.trim() });
      setSent(true);
      toast.success(`Email envoyé à ${prospect.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi");
    } finally { setLoading(false); }
  };

  const handleClose = () => {
    if (!sent && (subject.trim() || body.trim())) {
      if (!window.confirm("Votre message n'a pas été envoyé. Fermer quand même ?")) return;
    }
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="bg-white border-[#BFBFBF] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {sent ? "Email envoyé" : "Envoyer un email"}
          </DialogTitle>
          <DialogDescription className="text-[#6B6B6B] text-xs">
            {sent
              ? `Votre message a été envoyé à ${prospect.nom_entreprise}.`
              : `Envoyer un email à ${prospect.nom_entreprise} — cet email inclura un lien de désinscription.`
            }
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#3F6B6B]/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-[#3F6B6B]" />
            </div>
            <p className="text-sm text-[#333333] mb-1">Message envoyé avec succès</p>
            <p className="text-xs text-[#6B6B6B]">{prospect.email}</p>
            <Button onClick={onClose} className="mt-6 bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full px-8">Fermer</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Destinataire</Label>
              <div className="mt-1 flex items-center gap-2 bg-[#FBF9F5] border border-[#BFBFBF] rounded-md px-3 py-2.5">
                <Mail className="w-3.5 h-3.5 text-[#6B6B6B] flex-shrink-0" />
                <span className="text-sm text-[#2F4F4F] font-medium">{prospect.email}</span>
                <span className="text-xs text-[#6B6B6B] ml-auto">{prospect.nom_entreprise}</span>
              </div>
            </div>
            <div>
              <Label htmlFor="prospect-email-subject" className="text-xs text-[#6B6B6B] uppercase tracking-wider">Objet *</Label>
              <Input
                id="prospect-email-subject"
                data-testid="send-prospect-email-subject"
                value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Ex : Votre site de démonstration vous attend"
                className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] text-sm" autoFocus
              />
            </div>
            <div>
              <Label htmlFor="prospect-email-body" className="text-xs text-[#6B6B6B] uppercase tracking-wider">Message *</Label>
              <textarea
                id="prospect-email-body"
                data-testid="send-prospect-email-body"
                value={body} onChange={(e) => setBody(e.target.value)}
                placeholder="Rédigez votre message ici..."
                rows={8}
                className="mt-1 w-full bg-[#FBF9F5] border border-[#BFBFBF] rounded-md px-3 py-2.5 text-sm text-[#333333] placeholder:text-[#999] resize-none focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/20 focus:border-[#2F4F4F]/40 leading-relaxed"
              />
              <p className="text-[10px] text-[#999] mt-1">
                Signature et lien de désinscription ajoutés automatiquement.
              </p>
            </div>
            <DialogFooter className="flex gap-2 pt-2">
              <Button variant="outline" onClick={handleClose} className="border-[#BFBFBF] text-[#6B6B6B] hover:text-[#2F4F4F] rounded-full">Annuler</Button>
              <Button data-testid="send-prospect-email-submit" onClick={handleSend} disabled={!canSend || loading}
                className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Envoi..." : "Envoyer"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


const CHART_COLORS = ["#2F4F4F", "#3F6B6B", "#8BBFAD", "#BFBFBF"];
const tooltipStyle = { contentStyle: { background: "#fff", border: "1px solid #BFBFBF", borderRadius: "8px", fontSize: "12px" } };

function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await api.get("/admin/analytics");
        setData(d);
      } catch (err) { console.warn("Analytics load failed:", err?.message); } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#2F4F4F]" /></div>;
  if (!data) return <p className="text-center text-[#6B6B6B] py-12">Erreur de chargement</p>;

  const funnel = data.prospect_funnel || {};
  const funnelData = [
    { name: "Total", value: funnel.total || 0 },
    { name: "Envoyes", value: funnel.envoyes || 0 },
    { name: "Relances", value: funnel.relances || 0 },
    { name: "Convertis", value: funnel.convertis || 0 },
  ];
  const typeIcon = (t) => t === "payment" ? <DollarSign className="w-3.5 h-3.5 text-[#3F6B6B]" /> : t === "new_client" ? <UserPlus className="w-3.5 h-3.5 text-[#2F4F4F]" /> : <Mail className="w-3.5 h-3.5 text-[#6B6B6B]" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Analytics</h3>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#BFBFBF] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-[#2F4F4F]" />
            <p className="text-xs text-[#3F6B6B] font-medium uppercase tracking-wider">Nouveaux clients</p>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.client_growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#BFBFBF" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#777" }} />
              <YAxis tick={{ fontSize: 11, fill: "#777" }} allowDecimals={false} />
              <RTooltip {...tooltipStyle} />
              <Bar dataKey="new_clients" fill="#2F4F4F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Funnel + Plan Distribution */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[#BFBFBF] rounded-xl p-5">
          <p className="text-xs text-[#3F6B6B] font-medium uppercase tracking-wider mb-4">Tunnel de conversion</p>
          <div className="space-y-3">
            {funnelData.map((item, i) => {
              const maxVal = Math.max(funnel.total || 1, 1);
              const pct = Math.round((item.value / maxVal) * 100);
              return (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#6B6B6B]">{item.name}</span>
                    <span className="font-medium text-[#2F4F4F]">{item.value}</span>
                  </div>
                  <div className="h-2 bg-[#FBF9F5] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[i] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-[#BFBFBF] rounded-xl p-5">
          <p className="text-xs text-[#3F6B6B] font-medium uppercase tracking-wider mb-4">Repartition des forfaits</p>
          {data.plan_distribution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={data.plan_distribution} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35} strokeWidth={2}>
                  {data.plan_distribution.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <RTooltip {...tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-[#6B6B6B]/50 py-10 text-sm">Aucun client actif</p>
          )}
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {(data.plan_distribution || []).map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                <span className="text-[10px] text-[#6B6B6B]">{p.name} ({p.count})</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#BFBFBF] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-[#2F4F4F]" />
            <p className="text-xs text-[#3F6B6B] font-medium uppercase tracking-wider">Activite recente</p>
          </div>
          <ScrollArea className="h-[230px]">
            {(data.recent_activity || []).length === 0 ? (
              <p className="text-center text-[#6B6B6B]/50 py-10 text-sm">Aucune activite</p>
            ) : (
              <div className="space-y-3">
                {data.recent_activity.map((a, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-[#FBF9F5] flex items-center justify-center flex-shrink-0 mt-0.5">{typeIcon(a.type)}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-[#333] truncate">{a.description}</p>
                      <p className="text-[10px] text-[#999]">{a.date ? new Date(a.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
